import { Link } from 'react-router-dom'
import { ArrowRight, ArrowUpRight, Clock, CheckCircle2 } from 'lucide-react'
import publications from '@/data/publications'
import { profile } from '@/data/site'
import { SectionHeading } from '@/components/ui/section-heading'
import { SpotlightCard } from '@/components/ui/spotlight-card'
import { Reveal } from '@/components/ui/reveal'
import { AuthorList } from './AuthorList'

export function Publications() {
  return (
    <section id="publications" aria-labelledby="publications-title" className="relative py-24 md:py-32">
      <div className="container">
        <SectionHeading
          id="publications-title"
          eyebrow="Publications"
          title={
            <>
              Peer-reviewed work in <span className="font-serif italic text-zinc-300">vision</span> &amp;{' '}
              <span className="font-serif italic text-zinc-300">signals</span>.
            </>
          }
          description="Conference papers on 3D scene understanding and cross-subject gesture recognition."
        />

        <ol className="space-y-5">
          {publications.map((pub, idx) => {
            const published = pub.status === 'Published'
            return (
              <Reveal as="li" key={pub.id} delay={idx * 0.08}>
                  <SpotlightCard glow={published ? '196 181 253' : '255 179 217'}>
                    <article className="grid gap-8 p-7 md:p-9 lg:grid-cols-[1fr_240px]">
                      <div>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-xs">
                          <span
                            className={
                              published
                                ? 'inline-flex items-center gap-1.5 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-2.5 py-1 font-medium text-emerald-300'
                                : 'inline-flex items-center gap-1.5 rounded-full border border-amber-300/20 bg-amber-300/10 px-2.5 py-1 font-medium text-amber-200'
                            }
                          >
                            {published ? <CheckCircle2 className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                            {published ? 'Published' : 'Accepted · in press'}
                          </span>
                          <span className="font-mono text-zinc-500">{pub.year}</span>
                        </div>

                        <h3 className="mt-5 text-balance text-2xl font-medium leading-snug tracking-tight text-white md:text-[28px]">
                          <Link to={`/publication/${pub.id}/`} className="transition-colors hover:text-lilac">
                            {pub.title}
                          </Link>
                        </h3>
                        <p className="mt-3 text-sm text-zinc-500">{pub.conference}</p>
                        <AuthorList authors={pub.authors} highlight={profile.name} className="mt-4" />

                        <p className="mt-5 max-w-2xl text-pretty leading-relaxed text-zinc-400">{pub.summary}</p>

                        <ul className="mt-6 flex flex-wrap gap-2" aria-label="Keywords">
                          {pub.keywords.map((k) => (
                            <li key={k} className="chip">
                              {k}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="flex flex-col gap-3 lg:border-l lg:border-white/[0.06] lg:pl-8">
                        {pub.metrics && (
                          <dl className="grid grid-cols-3 gap-3 lg:grid-cols-1">
                            {pub.metrics.map((m) => (
                              <div key={m.label} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] px-4 py-3">
                                <dt className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">{m.label}</dt>
                                <dd className="mt-1 text-lg font-medium text-white">{m.value}</dd>
                              </div>
                            ))}
                          </dl>
                        )}
                        <div className="mt-auto flex flex-wrap gap-2 pt-2 lg:flex-col">
                          <Link
                            to={`/publication/${pub.id}/`}
                            className="group/btn inline-flex items-center justify-center gap-2 rounded-full bg-zinc-50 px-4 py-2.5 text-sm font-medium text-ink transition hover:bg-white"
                          >
                            Read summary
                            <ArrowRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-0.5" />
                          </Link>
                          {pub.publisherLink && (
                            <a
                              href={pub.publisherLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm font-medium text-zinc-200 transition hover:border-white/20 hover:bg-white/[0.08]"
                            >
                              IEEE Xplore <ArrowUpRight className="h-4 w-4" />
                            </a>
                          )}
                        </div>
                        {pub.doi && <p className="font-mono text-[11px] text-zinc-500">DOI {pub.doi}</p>}
                      </div>
                    </article>
                  </SpotlightCard>
              </Reveal>
            )
          })}
        </ol>
      </div>
    </section>
  )
}
